// Bloque de domicilio compartido por los pasos 02 y 10: lista lo que Fiado ya tiene guardado y deja
// agregar otro con el buscador. Elegir un candidato NO guarda; guardar es un botón aparte.
import { useId, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { Home, MapPin } from 'lucide-react';
import { Badge, Button } from '@/kit';
import { cn } from '@/lib/cn';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { useAddressSearch, useConfirmAddress } from '../api/useWizard';
import { wizardErrorMsg } from '../api/wizardError';
import type { AddressCandidate, SavedAddress } from '../types';
import { AddressCombobox, AddressSaveConfirm } from './AddressCombobox';

/** Instrucción por defecto: el vendedor tiene que ELEGIR dónde vive el cliente, no llenar un campo. */
const DEFAULT_INSTRUCTION =
    'Selecciona el domicilio donde vive el cliente. Si no aparece en la lista, búscalo y agrégalo.';

export interface AddressBlockProps {
    sessionId: string;
    /** Domicilios que Fiado YA tiene guardados. Fuente: address-lambda vía el wizard. */
    addresses: SavedAddress[];
    /** El domicilio del paso ya está resuelto ⇒ el buscador arranca cerrado, detrás de `changeLabel`. */
    settled: boolean;
    /** Qué decir arriba de la lista cuando hay domicilios guardados. */
    savedHint: string;
    /** Qué decir cuando el expediente no tiene ninguno. */
    emptyHint: string;
    /**
     * La lista todavía se está leyendo. Sin esto, el primer pintado le dice al vendedor que el
     * cliente no tiene domicilio y un segundo después se desdice.
     */
    loading?: boolean;
    /** Etiqueta del botón que reabre el buscador. */
    changeLabel: string;
    /** Acción del paso sobre el domicilio ya guardado (paso 10: «Sí, es el actual»). */
    confirmAction?: ReactNode;
    /** Título del contenedor. Se omite cuando el paso ya titula la sección que lo envuelve. */
    title?: string;
    /** Qué tiene que hacer el vendedor aquí, en una frase. */
    instruction?: string;
}

export function AddressBlock({
    sessionId,
    addresses,
    settled,
    savedHint,
    emptyHint,
    loading = false,
    changeLabel,
    confirmAction,
    title,
    instruction = DEFAULT_INSTRUCTION,
}: AddressBlockProps) {
    const confirmAddress = useConfirmAddress(sessionId);
    const headingId = useId();
    const [query, setQuery] = useState('');
    /**
     * Elegir NO es guardar. Antes un clic en un candidato mandaba el domicilio al expediente de
     * inmediato: como la llamada tarda, el vendedor no alcanzaba a ver qué había elegido y ya estaba
     * guardado — así se agregaron domicilios sin querer. Ahora el clic solo selecciona y hay un botón
     * aparte, igual que la selección de equipo del paso 03.
     */
    const [chosen, setChosen] = useState<AddressCandidate | null>(null);
    /** Reabre el buscador con el domicilio ya resuelto, sin perder lo que está guardado. */
    const [changing, setChanging] = useState(false);
    // Sin debounce, cada tecla a partir de la tercera era una llamada a AWS Place: el vendedor
    // escribiendo "Av. Constituyentes" disparaba una docena, y las respuestas llegaban desordenadas.
    const debouncedQuery = useDebounce(query.trim(), 350);
    const {
        data: candidates,
        isFetching: searching,
        isError: searchFailed,
        refetch: retrySearch,
    } = useAddressSearch(sessionId, debouncedQuery);

    const hasSaved = addresses.length > 0;
    const searchOpen = !settled || changing;

    const closeSearch = (): void => {
        setChanging(false);
        setQuery('');
        setChosen(null);
    };

    const save = (candidate: AddressCandidate): void => {
        void confirmAddress
            .mutateAsync({ useIne: false, address: candidate.domicilio })
            .then(() => {
                closeSearch();
                toast.success('Domicilio guardado');
            })
            .catch((e: unknown) => {
                toast.error(
                    wizardErrorMsg(e, 'No se pudo guardar el domicilio — intenta de nuevo.'),
                );
            });
    };

    return (
        <section
            className="rounded-[10px] border border-border bg-card px-3.5 py-3"
            aria-labelledby={title ? headingId : undefined}
        >
            {/* El bloque se lee como un paso con propósito, no como un campo suelto: primero qué
                hay que hacer, después la lista y el buscador. */}
            <div className="mb-3 flex items-start gap-2.5">
                <span className="mt-px inline-flex size-7 flex-none items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Home className="size-4" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                    {title ? (
                        <p id={headingId} className="text-[13px] font-semibold leading-[1.3]">
                            {title}
                        </p>
                    ) : null}
                    <p
                        className={cn(
                            'text-[12px] leading-[1.45] text-muted-foreground',
                            title && 'mt-0.5',
                        )}
                    >
                        {instruction}
                    </p>
                </div>
            </div>

            {/* Los domicilios salen de Fiado (address-lambda), NO del espejo de la sesión: el del
                INE ya viajó allá al verificarse el KYC. Leer el espejo fue justo lo que hizo que
                esta pantalla dijera "—" mientras el dato ya existía. */}
            {hasSaved ? (
                <>
                    <p className="mb-2 text-[12px] text-muted-foreground">{savedHint}</p>
                    {addresses.map((d) => (
                        <div
                            key={d.etiqueta}
                            className="mb-2 flex items-start gap-2.5 rounded-md border border-border bg-muted/40 px-3 py-2.5"
                        >
                            <MapPin
                                className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                                aria-hidden
                            />
                            <p className="min-w-0 flex-1 break-words text-[12.5px] leading-relaxed">
                                {d.etiqueta}
                            </p>
                            {/* Cuál es el principal se decide de un vistazo: en gris de 11px se
                                perdía contra la etiqueta del domicilio. */}
                            {d.esPrincipal && (
                                <Badge
                                    variant="info"
                                    className="mt-px flex-none px-2 py-0 text-[10px] uppercase tracking-[0.04em]"
                                >
                                    Principal
                                </Badge>
                            )}
                        </div>
                    ))}
                </>
            ) : (
                <p className="mb-2 text-[12px] text-muted-foreground">
                    {loading ? 'Consultando el domicilio del cliente…' : emptyHint}
                </p>
            )}

            {/* Resolver el domicilio no puede ser una puerta de una sola vía: el vendedor se equivoca
                de candidato, o el cliente dice el domicilio nuevo después. */}
            {settled && !changing && (
                <Button
                    size="sm"
                    variant="ghost"
                    className="mt-1 w-full sm:w-auto"
                    onClick={() => {
                        setChanging(true);
                    }}
                >
                    {changeLabel}
                </Button>
            )}

            {searchOpen && (
                <div className="mt-3 space-y-3">
                    <div className="flex flex-col gap-2 sm:flex-row">
                        {hasSaved && !changing && confirmAction}
                        {changing && (
                            <Button
                                size="sm"
                                variant="ghost"
                                className="w-full sm:w-auto"
                                disabled={confirmAddress.isPending}
                                onClick={closeSearch}
                            >
                                Cancelar
                            </Button>
                        )}
                    </div>

                    {/* Un domicilio NUEVO no reemplaza al anterior: se agrega como principal y Fiado
                        degrada al que estaba. Por eso aquí no se "edita", se agrega. */}
                    <div>
                        <AddressCombobox
                            label={hasSaved ? 'O captura otro' : 'Buscar domicilio'}
                            query={query}
                            onQueryChange={(value) => {
                                setQuery(value);
                                // Al cambiar la búsqueda, lo elegido deja de tener que ver con
                                // lo que se ve en pantalla.
                                setChosen(null);
                            }}
                            debouncedQuery={debouncedQuery}
                            searching={searching}
                            candidates={candidates ?? []}
                            searchFailed={searchFailed}
                            onRetrySearch={() => void retrySearch()}
                            chosen={chosen}
                            onChoose={setChosen}
                        />

                        {chosen && (
                            <AddressSaveConfirm
                                label={chosen.etiqueta}
                                saving={confirmAddress.isPending}
                                onSave={() => {
                                    save(chosen);
                                }}
                                onDiscard={() => {
                                    setChosen(null);
                                }}
                            />
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}
